import type { ReactNode } from 'react';

import { Badge, type BadgeTone } from './ui/index.tsx';
import { formatMoney, formatMoneyCompact, formatMonth } from '../lib/money.ts';
import type { AgingBucket, DocumentStatus, DocumentType, MonthlyPoint } from '../lib/types.ts';
import './bits.css';

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */

export const DOCUMENT_LABELS: Record<DocumentType, string> = {
  invoice: 'Invoice',
  quotation: 'Quotation',
  proforma: 'Proforma invoice',
  purchase_order: 'Purchase order',
  delivery_challan: 'Delivery challan',
  credit_note: 'Credit note',
  debit_note: 'Debit note',
};

export const DOCUMENT_PLURALS: Record<DocumentType, string> = {
  invoice: 'Invoices',
  quotation: 'Quotations',
  proforma: 'Proforma invoices',
  purchase_order: 'Purchase orders',
  delivery_challan: 'Delivery challans',
  credit_note: 'Credit notes',
  debit_note: 'Debit notes',
};

const STATUS_TONES: Record<DocumentStatus, BadgeTone> = {
  draft: 'neutral',
  sent: 'info',
  partially_paid: 'warn',
  paid: 'ok',
  overdue: 'bad',
  cancelled: 'neutral',
  accepted: 'ok',
  declined: 'bad',
  converted: 'accent',
};

const STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  partially_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  accepted: 'Accepted',
  declined: 'Declined',
  converted: 'Converted',
};

export function StatusBadge({ status }: { status: DocumentStatus }) {
  return <Badge tone={STATUS_TONES[status] ?? 'neutral'}>{STATUS_LABELS[status] ?? status}</Badge>;
}

/* -------------------------------------------------------------------------- */
/* Page header                                                                 */
/* -------------------------------------------------------------------------- */

export function PageHead({
  title,
  lede,
  actions,
}: {
  title: string;
  lede?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      <div>
        <h1 className="page-head__title">{title}</h1>
        {lede && <p className="page-head__lede">{lede}</p>}
      </div>
      {actions && <div className="row row--wrap">{actions}</div>}
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Stats                                                                       */
/* -------------------------------------------------------------------------- */

export function StatBand({ children }: { children: ReactNode }) {
  return <div className="stat-band">{children}</div>;
}

export function Stat({
  label,
  value,
  note,
  tone,
  lead,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'ok' | 'bad' | 'warn';
  lead?: boolean;
}) {
  return (
    <div className={`stat${lead ? ' stat--lead' : ''}`}>
      <span className="stat__label">{label}</span>
      <span className={`stat__value${tone ? ` stat__value--${tone}` : ''}`}>{value}</span>
      {note && <span className="stat__note">{note}</span>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Charts                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Revenue against expenses, drawn with divs.
 *
 * Six to twelve columns of two series does not justify a charting library or
 * the bundle it brings — and CSS bars inherit the theme for free.
 */
export function MonthlyChart({
  series,
  currency,
}: {
  series: MonthlyPoint[];
  currency: string;
}) {
  const peak = Math.max(
    1,
    ...series.map((point) => Math.max(point.invoicedMinor, point.expensesMinor)),
  );

  return (
    <div className="stack stack-3">
      <div className="chart" role="img" aria-label={describeSeries(series, currency)}>
        {series.map((point) => (
          <div className="chart__col" key={point.month}>
            <div className="chart__stack">
              <div
                className="chart__bar"
                style={{ height: `${Math.max((point.invoicedMinor / peak) * 100, 1)}%` }}
                title={`${formatMonth(point.month)} revenue ${formatMoney(point.invoicedMinor, currency)}`}
              />
              <div
                className="chart__bar chart__bar--expense"
                style={{ height: `${Math.max((point.expensesMinor / peak) * 100, 1)}%` }}
                title={`${formatMonth(point.month)} expenses ${formatMoney(point.expensesMinor, currency)}`}
              />
            </div>
            <span className="chart__label">{formatMonth(point.month)}</span>
          </div>
        ))}
      </div>
      <div className="chart__legend">
        <span className="chart__key">
          <span className="chart__swatch" /> Revenue, net of tax
        </span>
        <span className="chart__key">
          <span className="chart__swatch chart__swatch--expense" /> Expenses
        </span>
      </div>
    </div>
  );
}

function describeSeries(series: MonthlyPoint[], currency: string): string {
  if (series.length === 0) return 'No data yet';
  const total = series.reduce((sum, point) => sum + point.invoicedMinor, 0);
  return `Revenue and expenses across ${series.length} months. Total revenue ${formatMoney(total, currency)}.`;
}

/** How overdue the receivables are, as proportional bars. */
export function AgingChart({
  buckets,
  currency,
}: {
  buckets: AgingBucket[];
  currency: string;
}) {
  const total = Math.max(1, ...buckets.map((bucket) => bucket.amountMinor));
  const tones = ['ok', '', 'warn', 'warn', 'bad'];

  return (
    <div className="aging">
      {buckets.map((bucket, index) => (
        <div className="aging__row" key={bucket.label}>
          <span className="muted">{bucket.label}</span>
          <span className="aging__track">
            <span
              className={`aging__fill${tones[index] ? ` aging__fill--${tones[index]}` : ''}`}
              style={{ width: `${(bucket.amountMinor / total) * 100}%` }}
            />
          </span>
          <span className="aging__amount">
            {formatMoneyCompact(bucket.amountMinor, currency)}
            {bucket.count > 0 && <span className="faint"> · {bucket.count}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
